import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import './network-three.css';
import NetworkSVG from './NetworkSVG';

const NetworkThree = ({ className = '', small = false, style = {}, ...rest }) => {
  const mountRef = useRef(null);

  useEffect(() => {
    // Respect reduced motion: fallback to SVG
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return; // render nothing (SVG will be used by parent)

    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    const width = mount.clientWidth || 360;
    const height = mount.clientHeight || 240;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 0, 200);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0); // transparent
    mount.appendChild(renderer.domElement);

    // create some points
    const nodeCount = small ? 6 : 12;
    const positions = new Float32Array(nodeCount * 3);
    for (let i = 0; i < nodeCount; i++) {
      positions[i * 3 + 0] = (Math.random() - 0.5) * 260; // x
      positions[i * 3 + 1] = (Math.random() - 0.5) * 160; // y
      positions[i * 3 + 2] = (Math.random() - 0.5) * 80;  // z
    }

    const nodesGeometry = new THREE.BufferGeometry();
    nodesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const nodesMaterial = new THREE.PointsMaterial({
      size: small ? 10 : 6,
      vertexColors: false,
      color: small ? 0xffffff : 0x00e6c3,
      transparent: true,
      opacity: small ? 1.0 : 0.95,
      blending: small ? THREE.AdditiveBlending : THREE.NormalBlending
    });
    const points = new THREE.Points(nodesGeometry, nodesMaterial);
    scene.add(points);

    // lines (connect some pairs)
    const lineMaterial = new THREE.LineBasicMaterial({ color: 0x6a5cff, transparent: true, opacity: 0.85 });
    const linePositions = [];
    for (let i = 0; i < nodeCount; i++) {
      for (let j = i + 1; j < nodeCount; j++) {
        if (Math.random() > 0.66) continue; // sparse connections
        linePositions.push(positions[i * 3 + 0], positions[i * 3 + 1], positions[i * 3 + 2]);
        linePositions.push(positions[j * 3 + 0], positions[j * 3 + 1], positions[j * 3 + 2]);
      }
    }

    const linesGeo = new THREE.BufferGeometry();
    linesGeo.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
    const lines = new THREE.LineSegments(linesGeo, lineMaterial);
    scene.add(lines);

    // subtle ambient light and a soft point light for depth
    const amb = new THREE.AmbientLight(0xffffff, 0.15);
    scene.add(amb);
    const pLight = new THREE.PointLight(0x00e6c3, 0.6);
    pLight.position.set(50, 50, 120);
    scene.add(pLight);

    // group animation
    let frameId;
    const animate = () => {
      points.rotation.y += 0.0018;
      lines.rotation.y -= 0.0012;
      points.rotation.x += 0.0006;
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    animate();

    // handle resize
    const handleResize = () => {
      const w = mount.clientWidth || 360;
      const h = mount.clientHeight || 240;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    // cleanup
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      nodesGeometry.dispose();
      linesGeo.dispose();
      // remove canvas
      mount.removeChild(renderer.domElement);
    };
  }, [small]);

  // If reduced motion is set, return the SVG fallback for accessibility
  const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    return <div className={`network-svg-fallback ${className} ${small ? 'is-small' : ''}`} style={style} {...rest}><NetworkSVG className={className} small={small} /></div>;
  }

  return <div className={`network-three ${className} ${small ? 'is-small' : ''}`} ref={mountRef} aria-hidden="true" style={style} {...rest} />;
};

export default NetworkThree;
